import {
  ActionIcon,
  Alert,
  Badge,
  Box,
  Button,
  Center,
  Divider,
  Drawer,
  Group,
  Indicator,
  Loader,
  Overlay,
  ScrollArea,
  Stack,
  Text,
  Title,
} from '@mantine/core';
import {
  useClickOutside,
  useDebouncedCallback,
  useQueue,
} from '@mantine/hooks';
import { hideNotification, notifications } from '@mantine/notifications';
import IconBellBolt from '@tabler/icons-react/dist/esm/icons/IconBellBolt.mjs';
import IconChevronRight from '@tabler/icons-react/dist/esm/icons/IconChevronRight.mjs';
import IconEraser from '@tabler/icons-react/dist/esm/icons/IconEraser.mjs';
import {
  type InfiniteData,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { useVirtualizer } from '@tanstack/react-virtual';
import dayjs from 'dayjs';
import isToday from 'dayjs/plugin/isToday';
import { useCallback, useMemo, useRef, useState, useTransition } from 'react';
import { useTranslation } from 'react-i18next';
import { CSSTransition, TransitionGroup } from 'react-transition-group';
import { NotificationsApi } from '@/entities/notifications/notification';
import {
  ContentRender,
  IconRender,
  NotificationItem,
  getNotificationClassNames,
  TitleRender,
} from './notification-item';
import classes from './notification-item.module.css';
import { useNotificationsLogStore } from '../model/notification-log-store';
import { NotificationSoundToggle } from '@/features/notification-sound';
import { WsTypes } from '@/shared/api/websocket-events';
import { useInfinitePaginationData } from '@/shared/lib/hooks/use-infinite-pagination-data';
import { useSocket } from '@/shared/api/use-socket';
import { Page } from '@/shared/api/pagination';
import { NoticeDTO } from '@/entities/notifications/notification';
import { getNextPageParamFn } from '@/shared/lib/utils/pagination/get-next-page-param';
import cs from 'clsx';

dayjs.extend(isToday);

const size = 20;

interface NotificationsListProps {
  rows: NoticeDTO[];

  scrollElement: HTMLElement | null;
  handleDelete: (id: string) => void;
  hasMounted: boolean;
}

export const NotificationsList = ({
  rows,
  scrollElement,
  handleDelete,
  hasMounted,
}: NotificationsListProps) => {
  const nodeRefs = useRef(
    new Map<string, React.MutableRefObject<HTMLElement | null>>(),
  );

  const getNodeRef = (id: string) => {
    let ref = nodeRefs.current.get(id);
    if (!ref) {
      ref = { current: null };
      nodeRefs.current.set(id, ref);
    }
    return ref;
  };

  const rowVirtualizer = useVirtualizer({
    count: rows?.length || 0,
    enabled: rows.length > 0 && scrollElement !== null,

    getScrollElement: () => scrollElement,
    estimateSize: (index) => {
      const item = rows[index];
      const previousItem = rows[index - 1];

      const currentDay = dayjs(item.created_at).startOf('day');
      const previousDay = previousItem
        ? dayjs(previousItem.created_at).startOf('day')
        : null;

      return !currentDay.isSame(previousDay) ? 120 : 70;
    },
    measureElement:
      typeof window !== 'undefined' &&
      navigator.userAgent.indexOf('Firefox') === -1
        ? (el) => el?.getBoundingClientRect().height
        : undefined,
    gap: 10,
  });

  const _setNodeRef = useCallback(
    (id: string) => (el: HTMLElement | null) => {
      const ref = nodeRefs.current.get(id);
      if (ref) {
        rowVirtualizer.measureElement(el);
        ref.current = el;
      }
    },
    // rowVirtualizer is stable across renders so this is safe
    [rowVirtualizer],
  );

  const isScrolling = rowVirtualizer.isScrolling;
  const shouldAnimate = !isScrolling && hasMounted;

  return (
    <Stack
      component={TransitionGroup}
      style={{
        height: `${rowVirtualizer.getTotalSize()}px`,
        width: '100%',
        position: 'relative',
      }}
    >
      {rowVirtualizer.getVirtualItems().map((vc) => {
        const index = vc.index;
        const item = rows[index];

        const currentDay = dayjs(item.created_at).startOf('day');
        const previousItem = rows[index - 1];
        const previousDay = previousItem
          ? dayjs(previousItem.created_at).startOf('day')
          : null;

        const showDivider = index === 0 || !currentDay.isSame(previousDay);

        const nodeRef = getNodeRef(item.id);

        return (
          <CSSTransition
            key={item.id}
            nodeRef={nodeRef}
            timeout={shouldAnimate ? 250 : 0}
            exit={false}
          >
            {(state) => (
              <Box
                ref={_setNodeRef(item.id)}
                data-index={index}
                className={cs(
                  classes.notification,
                  shouldAnimate && classes.animate,
                  shouldAnimate && classes[state],
                )}
                style={{
                  position: 'absolute',
                  top: vc.start,
                  width: '100%',
                  transform: 'translateX(0)',
                }}
              >
                <Stack>
                  {showDivider && (
                    <Divider
                      label={
                        <Badge variant='default'>
                          {currentDay.isToday()
                            ? 'Today'
                            : currentDay.format('DD MMMM YYYY')}
                        </Badge>
                      }
                      pt={index === 0 ? undefined : 'lg'}
                      pb={0}
                      labelPosition='left'
                    />
                  )}
                  <NotificationItem
                    item={item}
                    onDelete={() => handleDelete(item.id)}
                  />
                </Stack>
              </Box>
            )}
          </CSSTransition>
        );
      })}
    </Stack>
  );
};

export const NotificationsLog = () => {
  const { opened, setOpened } = useNotificationsLogStore(
    ({ opened, setOpened }) => ({ opened, setOpened }),
  );
  const [_isPending, startTransition] = useTransition();
  const { queue, add } = useQueue({
    initialValues: [],
    limit: Infinity,
  });

  const { t } = useTranslation('translation');

  const [hasMounted, setHasMounted] = useState(false);

  // Holds the resolved ScrollArea viewport element.
  // Storing it in state means React re-renders NotificationsList
  // as soon as the viewport mounts, giving the virtualizer a real
  // scroll container on the very first render (and after HMR).
  const [scrollElement, setScrollElement] = useState<HTMLElement | null>(null);

  // Keep an imperative ref in sync for any code that needs it outside React.
  const parentRef = useRef<HTMLElement | null>(null);

  const handleViewportRef = useCallback((el: HTMLElement | null) => {
    parentRef.current = el;
    setScrollElement(el);
  }, []);

  const {
    data,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    refetch,
  } = useInfiniteQuery({
    queryKey: ['notifications-log', opened],
    queryFn: ({ pageParam = 1 }) =>
      NotificationsApi.getNotices({
        params: {
          page: pageParam,
          size,
          sort: JSON.stringify([{ field: 'created_at', direction: 'desc' }]),
        },
      }).then((res) => res.data),
    getNextPageParam: getNextPageParamFn,
    enabled: opened,
    cacheTime: 0,
    staleTime: 0,
  });

  const { mutate: showNotification } = useMutation({
    mutationKey: ['show-notification'],
    mutationFn: (id: string) =>
      NotificationsApi.getNotice(id).then((res) => res.data),
    onSuccess: (item) => {
      if (!opened)
        add(
          notifications.show({
            icon: <IconRender data={item.data} />,
            title: (
              <TitleRender data={item.data} created_at={item.created_at} />
            ),
            message: <ContentRender data={item.data} />,
            color: 'rgba(0,0,0,0)',
            classNames: getNotificationClassNames(item.data),
            withBorder: true,
            mah: 'fit-content',
            autoClose: false,
          }),
        );
    },
  });

  const { data: countData, refetch: refetchCount } = useQuery({
    queryKey: ['notice-count'],
    queryFn: () => NotificationsApi.getNoticesCount().then((res) => res.data),
    cacheTime: 0,
    staleTime: 0,
  });

  const handleClose = () => {
    setOpened(false);
    refetchCount();
    setHasMounted(false);
  };

  const debouncedRefetch = useDebouncedCallback((shouldRefetchLog: boolean) => {
    refetchCount();
    if (shouldRefetchLog) {
      refetch();
    }
  }, 1000);

  useSocket({
    type: WsTypes.NOTICE,
    handler: ({ value }) => {
      debouncedRefetch(opened);
      if (!opened) {
        showNotification(value['id']);
      }
    },
    onReconnect: () => {
      refetch();
      refetchCount();
    },
  });

  const queryClient = useQueryClient();

  const { mutate: remove } = useMutation({
    mutationKey: ['delete-notices'],
    onMutate: () => {
      queryClient.setQueryData<InfiniteData<Page<NoticeDTO>>>(
        ['notifications-log', opened],
        (p) => ({ ...p, pages: [] }),
      );
    },
    mutationFn: () => NotificationsApi.deleteNotices().then((res) => res.data),
    onSuccess: () => {
      refetch();
      refetchCount();
    },
  });

  const rows = useInfinitePaginationData(data);

  const ref = useClickOutside(handleClose);

  const _isListEmpty = useMemo(() => rows?.length === 0, [rows]);

  const handleClear = useCallback(() => {
    remove();
  }, [remove]);

  const handleOpen = useCallback(() => {
    startTransition(() => {
      queue.forEach((x) => {
        hideNotification(x);
      });
      setOpened(true);
    });
  }, [queue, setOpened]);

  const handleDelete = useCallback(() => {
    refetchCount();
    refetch();
  }, [refetch, refetchCount]);

  const handleMount = useDebouncedCallback(() => {
    setHasMounted(true);
  }, 0);

  const isInitialLoading = isLoading && rows.length === 0;
  const isEmpty = !isLoading && rows.length === 0;

  return (
    <>
      <Indicator
        inline
        label={countData?.count}
        disabled={!countData?.count}
        size={14}
      >
        <ActionIcon
          data-testid='notifications-log-btn'
          variant={'light'}
          onClick={handleOpen}
        >
          <IconBellBolt size={20} />
        </ActionIcon>
      </Indicator>

      <Drawer.Root
        position={'right'}
        size={'auto'}
        p={0}
        classNames={{
          content: '!bg-transparent !flex !flex-col',
        }}
        styles={{ content: { boxShadow: 'none' } }}
        opened={opened}
        onClose={handleClose}
        onEnterTransitionEnd={handleMount}
      >
        <Drawer.Content ref={ref} w={745} pl={10} className='!animate-fade'>
          <Overlay
            backgroundOpacity={0.35}
            blur={15}
            zIndex={0}
            style={{ backgroundColor: 'inherit' }}
            className='mask-render-[linear-gradient(90deg,rgba(0,0,0,0)0%,rgba(1,0,0,0.43)2%,rgba(2,0,0,1)15%,rgba(0,0,0,1)100%)]'
          />
          <Drawer.Header mih={80} className={'!block !bg-transparent !h-fit'}>
            <Stack gap={0} pl={3}>
              <Group justify={'space-between'} align={'center'}>
                <Title order={3}>
                  {t('log.title', { ns: 'notifications' })}
                </Title>
                <Group>
                  <NotificationSoundToggle />
                  <Button
                    size={'compact-xs'}
                    leftSection={<IconEraser size={16} />}
                    onClick={() => handleClear()}
                  >
                    {t('action.clear', { ns: 'translation' })}
                  </Button>
                  <ActionIcon
                    size={'sm'}
                    variant={'default'}
                    onClick={handleClose}
                  >
                    <IconChevronRight size={16} />
                  </ActionIcon>
                </Group>
              </Group>
              <Text size={'xs'} c={'dimmed'}>
                {t('text.total')}: {rows.length}
              </Text>
            </Stack>
          </Drawer.Header>

          {isInitialLoading ? (
            <Center ml={30}>
              <Loader />
            </Center>
          ) : isEmpty ? (
            <Center
              ml={30}
              className='!h-auto absolute left-[50%] top-[50%] -translate-x-1/2 -translate-y-1/2'
            >
              <Alert
                className='!w-fit text-nowrap'
                variant='default'
                title={t('log.empty', { ns: 'notifications' })}
              />
            </Center>
          ) : (
            <Drawer.Body pr={0} pb='md' className='overflow-hidden grow'>
              <ScrollArea
                className='h-full overflow-hidden'
                classNames={{ viewport: 'pr-[12px]' }}
                // Callback ref: fires with the element on mount and null on
                // unmount. Storing it in state triggers a re-render so the
                // virtualizer sees a real scroll container immediately.
                viewportRef={handleViewportRef}
                onBottomReached={
                  hasNextPage && !isFetchingNextPage
                    ? () => fetchNextPage()
                    : undefined
                }
              >
                <NotificationsList
                  hasMounted={hasMounted}
                  rows={rows}
                  scrollElement={scrollElement}
                  handleDelete={handleDelete}
                />
              </ScrollArea>
            </Drawer.Body>
          )}
        </Drawer.Content>
      </Drawer.Root>
    </>
  );
};
